require("dotenv").config();

const axios = require("axios");

const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

const formatDate = (date) =>
  date.toLocaleDateString("en-GB").split("/").join("-");

// Get location from IP address (fallback method)
async function getLocationFromIP() {
  try {
    console.log("Attempting IP-based geolocation...");
    const response = await axios.get("https://get.geojs.io/v1/ip/geo.json", {
      timeout: 5000, // 5 second timeout
    });
    const data = response.data;

    if (!data || !data.latitude || !data.longitude) {
      console.error("Invalid IP geolocation response");
      return null;
    }

    console.log(`IP geolocation found: ${data.city}, ${data.country}`);

    return {
      lat: String(data.latitude),
      lng: String(data.longitude),
      currentLocation: {
        city: data.city ? [data.city] : undefined,
        country: data.country_code ? [data.country_code] : undefined,
        state: data.region ? [data.region] : undefined,
      },
      source: "ip", // Mark as IP-based geolocation
    };
  } catch (error) {
    console.error("IP geolocation failed:", error.message);
    return null;
  }
}

export async function getTimings() {
  try {
    const location = await getCurrentLocation();
    if (!location) {
      console.error("Could not get location");
      return null;
    }

    console.log("Location retrieved:", location);

    const today = formatDate(new Date());
    const tomorrow = formatDate(new Date(Date.now() + 86400000));

    // Validate we have usable location data
    const hasCoordinates = location.lat && location.lng;
    const hasCity =
      location.currentLocation?.city?.[0] ||
      location.currentLocation?.state?.[0];

    console.log("hasCoordinates:", hasCoordinates, "hasCity:", hasCity);

    if (!hasCoordinates && !hasCity) {
      console.error("No valid location data (neither coordinates nor city)");
      return null;
    }

    const params = hasCoordinates
      ? {
          latitude: location.lat,
          longitude: location.lng,
        }
      : {
          city:
            location.currentLocation?.city?.[0] ??
            location.currentLocation?.state?.[0],
          country: location.currentLocation?.country?.[0],
        };

    console.log("Fetching prayer times with params:", params);

    const [prayerTimesToday, prayerTimesTomorrow] = await Promise.all([
      getPrayerTimes({ ...params, date: today }),
      getPrayerTimes({ ...params, date: tomorrow }),
    ]);

    // Validate API responses
    if (!prayerTimesToday || !prayerTimesTomorrow) {
      console.error("Failed to fetch prayer times from API");
      return null;
    }

    if (!prayerTimesToday?.timings || !prayerTimesTomorrow?.timings) {
      console.error("Invalid prayer times data structure");
      return null;
    }

    return {
      location,
      prayerTimesToday: {
        date: prayerTimesToday.date?.gregorian?.date,
        timings: prayerTimesToday.timings,
      },
      prayerTimesTomorrow: {
        date: prayerTimesTomorrow.date?.gregorian?.date,
        timings: prayerTimesTomorrow.timings,
      },
    };
  } catch (error) {
    console.error("Error fetching prayer times:", error);
    return null;
  }
}

const getCurrentLocation = () => {
  return new Promise(async (resolve) => {
    let cachedData = null;

    // Try to access localStorage safely
    try {
      cachedData = localStorage.getItem("cachedLocation");
    } catch (error) {
      console.warn("localStorage not available:", error);
    }

    // Check cache validity
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const cacheAge = Date.now() - (parsed.timestamp || 0);

        if (cacheAge < CACHE_EXPIRY_MS) {
          // If cache is from IP and browser geolocation is available, try browser first
          if (parsed.data?.source === "ip" && navigator.geolocation) {
            console.log(
              "Cached IP location found, but trying browser geolocation first...",
            );
            // Continue to geolocation attempt below instead of returning cache
          } else {
            console.log(
              "Using cached location from:",
              parsed.data?.source || "unknown",
            );
            resolve(parsed.data);
            return;
          }
        } else {
          console.log("Cache expired, fetching new location");
          try {
            localStorage.removeItem("cachedLocation");
          } catch (e) {
            console.warn("Could not remove cache:", e);
          }
        }
      } catch (e) {
        console.error("Error parsing cached location:", e);
        try {
          localStorage.removeItem("cachedLocation");
        } catch (err) {
          console.warn("Could not remove cache:", err);
        }
      }
    }

    // Try geolocation first if available
    if (navigator.geolocation) {
      console.log("Trying browser geolocation...");

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const coordinates = {
              lat: String(latitude),
              lng: String(longitude),
            };
            const location = await getLocation(coordinates);

            if (!location) {
              console.error("Failed to reverse geocode coordinates");
              // Still return coordinates even if reverse geocode fails
              const fallbackData = {
                lat: coordinates.lat,
                lng: coordinates.lng,
                currentLocation: null,
                source: "browser", // Still from browser geolocation
              };
              cacheAndResolve(fallbackData, resolve);
              return;
            }

            const locationData = {
              lat: location.lat,
              lng: location.lng,
              currentLocation: {
                country: location.currentLocation?.country,
                city: location.currentLocation?.city,
                state: location.currentLocation?.state,
                town: location.currentLocation?.town,
                county: location.currentLocation?.county,
              },
              source: "browser", // Mark as browser geolocation
            };

            cacheAndResolve(locationData, resolve);
          } catch (error) {
            console.error("Error processing geolocation:", error);
            // Fall back to IP geolocation
            await tryIPGeolocation(resolve);
          }
        },
        async (error) => {
          console.error("Geolocation error:", error.message || error);
          if (error.code === 1) {
            console.error("User denied geolocation permission");
          } else if (error.code === 2) {
            console.error("Position unavailable");
          } else if (error.code === 3) {
            console.error("Geolocation timeout");
          }
          // Fall back to IP geolocation
          await tryIPGeolocation(resolve);
        },
        {
          timeout: 10000, // 10 second timeout
          maximumAge: 0, // Don't use device cache
          enableHighAccuracy: false, // Faster, less battery
        },
      );
    } else {
      console.log("Geolocation not supported, trying IP-based location...");
      await tryIPGeolocation(resolve);
    }
  });
};

// Helper function to try IP geolocation
async function tryIPGeolocation(resolve) {
  const ipLocation = await getLocationFromIP();
  if (ipLocation) {
    cacheAndResolve(ipLocation, resolve);
  } else {
    console.error("All location methods failed");
    resolve(null);
  }
}

// Helper function to cache location and resolve
function cacheAndResolve(locationData, resolve) {
  try {
    localStorage.setItem(
      "cachedLocation",
      JSON.stringify({
        timestamp: Date.now(),
        data: locationData,
      }),
    );
  } catch (e) {
    console.error("Failed to cache location:", e);
  }
  resolve(locationData);
}

async function fetchWithRetry(url, params, retries = 3, delay = 1000) {
  for (let i = 0; i < retries; i++) {
    try {
      const { data } = await axios.get(url, { params });
      return data;
    } catch (error) {
      const errorMsg = error.response?.status
        ? `Status ${error.response.status}: ${error.response.statusText}`
        : error.message;
      console.error(
        `API request failed (attempt ${i + 1}/${retries}):`,
        errorMsg,
      );

      // Don't retry on client errors (4xx), only on server errors (5xx) and network errors
      if (
        error.response?.status &&
        error.response.status >= 400 &&
        error.response.status < 500
      ) {
        console.error("Client error - not retrying");
        return null;
      }

      if (i < retries - 1) {
        console.log(`Retrying in ${delay}ms...`);
        await new Promise((res) => setTimeout(res, delay));
      }
    }
  }
  console.error("All retry attempts failed");
  return null;
}

export async function getLocation({ lat, lng }) {
  try {
    console.log("Fetching location details from BigDataCloud...");

    const response = await axios.get(
      "https://api.bigdatacloud.net/data/reverse-geocode-client",
      {
        params: {
          latitude: lat,
          longitude: lng,
          localityLanguage: "en",
        },
        timeout: 5000, // 5 second timeout
      },
    );

    const data = response.data;

    if (!data) {
      console.error("No data returned from BigDataCloud API");
      return null;
    }

    // Map BigDataCloud response to match expected format (arrays like LocationIQ XML parser)
    const currentLocation = {
      city: data.city ? [data.city] : undefined,
      town: data.locality ? [data.locality] : undefined,
      state: data.principalSubdivision
        ? [data.principalSubdivision]
        : undefined,
      county: data.localityInfo?.administrative?.[0]?.name
        ? [data.localityInfo.administrative[0].name]
        : undefined,
      country: data.countryName ? [data.countryName] : undefined,
    };

    console.log(
      "Location found:",
      data.city || data.locality || data.principalSubdivision,
    );

    return {
      lat,
      lng,
      currentLocation,
    };
  } catch (error) {
    console.error("BigDataCloud reverse geocoding error:", error.message);
    return null;
  }
}

export async function getPrayerTimes({
  latitude,
  longitude,
  city,
  country,
  date,
}) {
  try {
    const isCoordinates = Boolean(latitude && longitude);
    const isCityBased = Boolean(city && country);

    // Validate we have required parameters
    if (!isCoordinates && !isCityBased) {
      console.error("getPrayerTimes: Missing required location parameters");
      return null;
    }

    if (!date) {
      console.error("getPrayerTimes: Missing date parameter");
      return null;
    }

    const url = `http://api.aladhan.com/v1/${isCoordinates ? "timings" : "timingsByCity"}/${date}`;
    const params = isCoordinates ? { latitude, longitude } : { city, country };

    const data = await fetchWithRetry(url, params);

    if (!data) {
      console.error("No response from Aladhan API");
      return null;
    }

    // Validate response structure
    if (!data.data || !data.data.timings) {
      console.error("Invalid Aladhan API response structure");
      return null;
    }

    return data.data;
  } catch (error) {
    console.error("Prayer times fetch error:", error);
    return null;
  }
}

// Function to manually set location (can be called from UI)
export function setManualLocation({ city, country, latitude, longitude }) {
  try {
    const locationData = {
      lat: latitude ? String(latitude) : null,
      lng: longitude ? String(longitude) : null,
      currentLocation:
        city && country
          ? {
              city: [city],
              country: [country],
              source: "manual", // Mark as manually entered
            }
          : null,
      manual: true, // Flag to indicate this was manually set
    };

    localStorage.setItem(
      "cachedLocation",
      JSON.stringify({
        timestamp: Date.now(),
        data: locationData,
      }),
    );

    console.log("Manual location saved:", city || `${latitude}, ${longitude}`);
    return true;
  } catch (error) {
    console.error("Failed to save manual location:", error);
    return false;
  }
}

// Function to clear cached location (useful for re-detecting)
export function clearLocationCache() {
  try {
    localStorage.removeItem("cachedLocation");
    console.log("Location cache cleared");
    return true;
  } catch (error) {
    console.error("Failed to clear location cache:", error);
    return false;
  }
}
